import React, { useState, useEffect } from 'react';
import { 
  FacebookIcon, 
  InstagramIcon, 
  LinkedInIcon, 
  XIcon
} from './SocialMediaIcons';
import { 
  CheckCircle,
  WarningCircle,
  Link
} from '@phosphor-icons/react';
import socialMediaApiService from '../services/socialMediaApiService';

const SocialMediaConnections = () => {
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await socialMediaApiService.getConnectionStatus();
      setConnections(status.connections || {});
    } catch (error) {
      console.error('Error loading connection status:', error);
      setError('Failed to load connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platform) => {
    try {
      setError(null);
      
      switch (platform) {
        case 'facebook':
          await socialMediaApiService.connectFacebook();
          break;
        case 'twitter':
          await socialMediaApiService.connectTwitter();
          break;
        case 'linkedin':
          await socialMediaApiService.connectLinkedIn();
          break;
        default:
          throw new Error(`Unsupported platform: ${platform}`);
      }
    } catch (error) {
      console.error(`Error connecting to ${platform}:`, error);
      setError(`Failed to connect to ${platform}: ${error.message}`);
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      setError(null);
      await socialMediaApiService.disconnectPlatform(platform);
      await loadConnectionStatus(); // Refresh status
    } catch (error) {
      console.error(`Error disconnecting from ${platform}:`, error);
      setError(`Failed to disconnect from ${platform}: ${error.message}`);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook':
        return FacebookIcon;
      case 'instagram':
        return InstagramIcon;
      case 'linkedin':
        return LinkedInIcon;
      case 'twitter':
      case 'x':
        return XIcon;
      default:
        return Link;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'facebook':
        return 'bg-blue-600';
      case 'instagram':
        return 'bg-gradient-to-r from-purple-500 to-pink-500';
      case 'linkedin':
        return 'bg-blue-700';
      case 'twitter':
      case 'x':
        return 'bg-black';
      default:
        return 'bg-gray-600';
    }
  };

  const platforms = [
    { id: 'facebook', name: 'Facebook', description: 'Post to Facebook pages and get engagement metrics' },
    { id: 'twitter', name: 'X (Twitter)', description: 'Post tweets and track engagement' },
    { id: 'linkedin', name: 'LinkedIn', description: 'Share professional content and measure reach' },
    { id: 'instagram', name: 'Instagram', description: 'Post images and stories (via Facebook)' }
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media Connections</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading connections...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Social Media Connections</h3>
        <button
          onClick={loadConnectionStatus}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {platforms.map((platform) => {
          const isConnected = connections[platform.id]?.connected;
          const PlatformIcon = getPlatformIcon(platform.id);
          const platformColor = getPlatformColor(platform.id);

          return (
            <div
              key={platform.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 rounded-lg ${platformColor} flex items-center justify-center text-white`}>
                  <PlatformIcon size={20} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{platform.name}</h4>
                  <p className="text-sm text-gray-500">{platform.description}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {isConnected ? (
                  <>
                    <div className="flex items-center text-green-600">
                      <CheckCircle size={16} className="mr-1" />
                      <span className="text-sm font-medium">Connected</span>
                    </div>
                    <button
                      onClick={() => handleDisconnect(platform.id)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center text-gray-400">
                      <WarningCircle size={16} className="mr-1" />
                      <span className="text-sm">Not Connected</span>
                    </div>
                    <button
                      onClick={() => handleConnect(platform.id)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    >
                      Connect
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Connect your social media accounts to enable direct posting</li>
          <li>• Posts will be published directly to your connected accounts</li>
          <li>• Track engagement metrics and performance</li>
          <li>• Your credentials are stored securely and encrypted</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialMediaConnections;
